import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Badge, Table, InputGroup, Spinner, Alert, Modal } from 'react-bootstrap';
import './Posts.css';
import api from "../../../api/axiosConfig";
import { useAuth } from '../../../context/AuthContext';

const Posts = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    fetchPosts();
  }, [user?.site_id]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const siteId = user?.site_id;
      if (!siteId) {
        setError('Site ID not found. Please contact support.');
        setLoading(false);
        return;
      }

      const response = await api.get(`/posts?site_id=${siteId}`);
      console.log("Posts API response:", response.data);
      if (response.data.success) {
        setPosts(response.data.data || []);
      } else {
        setError(response.data.message || 'Failed to fetch posts.');
      }
    } catch (err) {
      console.error(err);
      setError('Failed to fetch posts.');
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.post?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || 
      (filterStatus === 'published' && post.status === 1) || 
      (filterStatus === 'draft' && post.status === 0);
    return matchesSearch && matchesFilter;
  });

  const getStatusVariant = (status) => {
    switch (status) {
      case 1: return 'success';
      case 0: return 'secondary';
      default: return 'secondary';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1: return 'Published';
      case 0: return 'Draft';
      default: return 'Draft';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleEdit = (postId) => {
    navigate(`/site-admin/posts/edit/${postId}`);
  };

  const handleDelete = async (postId) => {
    if (window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      try {
        const siteId = user?.site_id;
        const response = await api.delete(`/posts/${postId}?site_id=${siteId}`);
        
        if (response.data.success) {
          setSuccessMessage('Post deleted successfully!');
          setPosts(posts.filter(post => post.id !== postId));
          setTimeout(() => setSuccessMessage(''), 3000);
        } else {
          setError('Failed to delete post.');
        }
      } catch (err) {
        console.error('Delete error:', err);
        setError('Failed to delete post.');
      }
    }
  };

  const handleStatusChange = async (postId, newStatus) => {
    try {
      const siteId = user?.site_id;
      const response = await api.put(`/posts/${postId}`, {
        site_id: siteId,
        status: newStatus,
        updated_by: user.id
      });

      if (response.data.success) {
        setSuccessMessage('Post status updated successfully!');
        setPosts(posts.map(post => 
          post.id === postId ? { ...post, status: newStatus } : post
        ));
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError('Failed to update post status.');
      }
    } catch (err) {
      console.error('Status update error:', err);
      setError('Failed to update post status.');
    }
  };

  const handleView = (post) => {
    setSelectedPost(post);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedPost(null);
  };

  if (loading) {
    return (
      <Container className="posts-container">
        <div className="loading-container">
          <Spinner animation="border" variant="primary" />
          <p className="loading-text">Loading posts...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="posts-container">
      {/* Header */}
      <div className="posts-header">
        <div className="header-content">
         
          <br />
          <p className="page-subtitle">Manage your blog posts and articles</p>
        </div>
        <Button
          variant="primary"
          className="new-post-btn"
          onClick={() => navigate('/site-admin/posts/create')}
        >
          <i className="fas fa-plus me-2"></i>
          New Post
        </Button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" className="posts-alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert variant="success" className="posts-alert">
          <i className="fas fa-check-circle me-2"></i>
          {successMessage}
        </Alert>
      )}

      {/* Filters and Search */}
      <Card className="filters-card">
        <Card.Body className="filters-body">
          <Row className="g-3">
            <Col md={6}>
              <InputGroup className="search-input-group">
                <InputGroup.Text className="search-icon">
                  <i className="fas fa-search"></i>
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search posts by title or content..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="status-filter"
              >
                <option value="all">All Status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <div className="posts-count">
                <span className="count-text">
                  {filteredPosts.length} {filteredPosts.length === 1 ? 'post' : 'posts'} found
                </span>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Posts Table */}
      <Card className="posts-table-card">
        <Card.Body className="posts-table-body">
          <div className="table-responsive">
            <Table hover className="posts-table">
              <thead className="table-header">
                <tr>
                  <th className="title-column">Title</th>
                  <th className="status-column">Status</th>
                  <th className="date-column">Date</th>
                  <th className="featured-column">Featured</th>
                  <th className="actions-column text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPosts.map((post) => (
                  <tr key={post.id} className="post-row">
                    <td className="title-cell">
                      <div className="post-title-wrapper">
                        <div className="post-title">{post.title || 'Untitled'}</div>
                        <div 
                          className="post-excerpt"
                          dangerouslySetInnerHTML={{ 
                            __html: post.post ? post.post.substring(0, 80) + '...' : 'No content' 
                          }} 
                        />
                      </div>
                    </td>
                    <td className="status-cell">
                      <Badge 
                        bg={getStatusVariant(post.status)} 
                        className="status-badge"
                      >
                        {getStatusText(post.status)}
                      </Badge>
                    </td>
                    <td className="date-cell">
                      <div className="date-info">
                        <i className="fas fa-calendar me-2"></i>
                        {formatDate(post.created_at)}
                      </div>
                    </td>
                    <td className="featured-cell">
                      {post.featured ? (
                        <Badge bg="warning" className="featured-badge">
                          <i className="fas fa-star me-1"></i>
                          Featured
                        </Badge>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="actions-cell text-end">
                      <div className="actions-wrapper">
                        <Button
                          variant="outline-info"
                          size="sm"
                          className="action-btn"
                          onClick={() => handleView(post)}
                          title="View Post"
                        >
                          <i className="fas fa-eye"></i>
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="action-btn"
                          onClick={() => handleEdit(post.id)}
                          title="Edit Post"
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                       
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="action-btn"
                          onClick={() => handleDelete(post.id)}
                          title="Delete Post"
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {filteredPosts.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">
                <i className="fas fa-search"></i>
              </div>
              <h5 className="empty-title">
                {searchTerm || filterStatus !== 'all' ? 'No posts found' : 'No posts yet'}
              </h5>
              <p className="empty-description">
                {searchTerm || filterStatus !== 'all'
                  ? 'Try adjusting your search or filter criteria'
                  : 'Get started by creating your first post'
                }
              </p>
              {!searchTerm && filterStatus === 'all' && (
                <Button
                  variant="primary"
                  className="empty-action-btn"
                  onClick={() => navigate('/site-admin/posts/create')}
                >
                  <i className="fas fa-plus me-2"></i>
                  Create Your First Post
                </Button>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* View Post Modal */}
      <Modal 
        show={showViewModal} 
        onHide={handleCloseViewModal} 
        size="lg" 
        centered
        className="post-view-modal"
      >
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title>
            <i className="fas fa-eye me-2"></i>
            Post Preview
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body-custom">
          {selectedPost && (
            <div className="post-preview">
              <div className="preview-header">
                <h2 className="preview-title">{selectedPost.title}</h2>
                <div className="preview-meta">
                  <Badge bg={getStatusVariant(selectedPost.status)} className="me-2">
                    {getStatusText(selectedPost.status)}
                  </Badge>
                  {selectedPost.featured && (
                    <Badge bg="warning">
                      <i className="fas fa-star me-1"></i>
                      Featured
                    </Badge>
                  )}
                  <span className="text-muted ms-2">
                    <i className="fas fa-calendar me-1"></i>
                    {formatDate(selectedPost.created_at)}
                  </span>
                </div>
              </div>
              <div className="preview-content">
                <div 
                  className="post-content-preview"
                  dangerouslySetInnerHTML={{ __html: selectedPost.post }} 
                />
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="modal-footer-custom">
          <Button variant="secondary" onClick={handleCloseViewModal}>
            Close
          </Button>
          <Button 
            variant="primary" 
            onClick={() => {
              handleCloseViewModal();
              handleEdit(selectedPost.id);
            }}
          >
            <i className="fas fa-edit me-2"></i>
            Edit Post
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Posts;